import { AppLink } from "@/components/ui/app-link";
import { projectColorVar } from "@/lib/project-color";
import { type ReactNode, createContext, useContext } from "react";

/**
 * Rendering a project as "a colour and a name".
 *
 * Colours are derived from the project id (see `lib/project-color.ts`), so a
 * component only needs `{ id, name }` — no payload anywhere had to grow a
 * `color` field. The rare per-project override arrives once, with the app
 * layout, and this context is how every render site gets to see it without
 * fetching anything of its own.
 *
 * The name is always rendered alongside the dot. The colour is a shortcut for
 * finding a row you're already looking for, never the thing that tells you
 * which project it is — which is what makes it safe for a colourblind reader,
 * and what makes a repeated hue across many projects a non-issue.
 */

const ProjectColorContext = createContext<Record<string, string>>({});

export function ProjectColorProvider({
  overrides,
  children,
}: {
  overrides: Record<string, string>;
  children: ReactNode;
}) {
  return <ProjectColorContext.Provider value={overrides}>{children}</ProjectColorContext.Provider>;
}

/** The CSS colour this project wears, override-aware. */
export function useProjectColor(projectId: string): string {
  const overrides = useContext(ProjectColorContext);
  return projectColorVar(projectId, overrides[projectId]);
}

/**
 * The colour on its own — for places too tight for a name, or where the name is
 * already right there in the surrounding markup.
 *
 * `aria-hidden` on purpose: it repeats information the adjacent text already
 * carries, so announcing it would just be noise.
 */
export function ProjectDot({
  projectId,
  className = "",
}: {
  projectId: string;
  className?: string;
}) {
  const color = useProjectColor(projectId);
  return (
    <span
      aria-hidden="true"
      data-testid="project-dot"
      data-project-id={projectId}
      style={{ backgroundColor: color }}
      className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${className}`}
    />
  );
}

/**
 * A project as it appears in a list: its colour, then its name, optionally
 * linked. `href` is a plain string so callers keep using whichever of the
 * `projectHref` wrappers they already have (several throw for Hub-only
 * projects, and each caller handles that its own way).
 */
export function ProjectTag({
  project,
  href,
  className = "",
  /** Extra content after the name — a customer name, a crown, a count. */
  children,
}: {
  project: { id: string; name: string };
  href?: string | null;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <span className={`inline-flex min-w-0 items-center gap-1.5 ${className}`}>
      <ProjectDot projectId={project.id} />
      {href ? (
        <AppLink href={href} className="truncate hover:underline">
          {project.name}
        </AppLink>
      ) : (
        <span className="truncate">{project.name}</span>
      )}
      {children}
    </span>
  );
}
